import React, { forwardRef, PropsWithChildren, useContext } from "react";

import {
  Dropdown as MagneticDropdown,
  type DropdownProps
} from "@magnetic/dropdown";
import { Popover } from "@magnetic/popover";
import { Text } from "@magnetic/text";

import { LoginContext } from "src/core/loginContext";

import { withAdminGuard } from "../hoc/withAdminGuard";
import { withEditModeGuard } from "../hoc/withEditModeGuard";
import { withReadOnlyGuard } from "../hoc/withReadOnlyGuard";

import { ButtonScope } from "./types";

type DropdownComponentProps = DropdownProps & {
  readonly tooltip?: string;
  /**
   * Controls which guards are applied to the dropdown.
   *
   * When omitted (default), no guards are applied — the dropdown
   * is always visible. Use `Dropdown.Item scope={ButtonScope.writeAccess}`
   * to hide individual items from read-only users.
   *
   * - `fabricEditModeOnly`: Hidden from read-only users and only
   *   visible when the fabric is in candidate-edit mode
   *   (CandidateMode.Edit). The read-only guard uses LoginContext;
   *   the edit-mode guard relies on DataProviderContext, which is
   *   fabric-scoped.
   *
   * - `writeAccess`: Only the read-only guard applies.
   *   Use on pages outside a fabric context (e.g. notifications,
   *   API tokens, subscriptions, inventory).
   *
   * - `adminOnly`: Hidden from both read-only and read-write users.
   *   Only visible to users with the ADMIN role.
   */
  readonly scope?: ButtonScope;
};

/**
 * Inner dropdown used by both the write-access and fabric-edit-mode variants.
 * Not exported — consumers should always use `Dropdown`.
 */
const DropdownInner = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<DropdownComponentProps>
>((props, ref) => {
  const { children, tooltip, ...rest } = props;

  const dropdown = (
    <>
      {/* Pass all remaining props to Dropdown, render children inside */}
      <MagneticDropdown ref={ref} {...rest}>
        {children}
      </MagneticDropdown>
    </>
  );

  if (tooltip) {
    return (
      <Popover
        kind="map"
        className="popover-tooltip"
        title={<Text size="p3">{tooltip}</Text>}
      >
        {dropdown}
      </Popover>
    );
  }

  return dropdown;
});

/**
 * Write-access variant: hidden for read-only users.
 * No fabric-scoped guards (edit mode / software upgrade).
 */
const WriteAccessDropdown = withReadOnlyGuard(DropdownInner);

/**
 * Fabric-edit-mode variant: hidden from read-only users
 * and hidden when the fabric is not in candidate-edit mode.
 */
const FabricEditModeDropdown = withReadOnlyGuard(
  withEditModeGuard(DropdownInner)
);

/**
 * Admin-only variant: hidden for non-admin users.
 * Only users with the ADMIN role can see this dropdown.
 */
const AdminOnlyDropdown = withAdminGuard(DropdownInner);

/**
 * A dropdown with configurable guard behavior.
 *
 * By default (no `scope`), no guards are applied — the dropdown is
 * always visible. Use `Dropdown.Item scope={ButtonScope.writeAccess}`
 * to hide individual items from read-only users.
 *
 * Use `scope=fabricEditModeOnly` on fabric-scoped pages where the
 * dropdown should be hidden from read-only users and only visible
 * when the fabric is in candidate-edit mode.
 *
 * Use `scope=writeAccess` on app-wide pages (notifications, API tokens, etc.)
 * where only the read-only guard should apply.
 */
const DropdownComponent = forwardRef<
  HTMLButtonElement,
  PropsWithChildren<DropdownComponentProps>
>((props, ref) => {
  const { scope, ...rest } = props;
  if (scope === ButtonScope.fabricEditModeOnly) {
    return <FabricEditModeDropdown {...rest} ref={ref} />;
  }
  if (scope === ButtonScope.writeAccess) {
    return <WriteAccessDropdown {...rest} ref={ref} />;
  }
  if (scope === ButtonScope.adminOnly) {
    return <AdminOnlyDropdown {...rest} ref={ref} />;
  }
  // No scope: no guards — the dropdown is visible to all users.
  // Use Dropdown.Item scope={...} to hide individual items based on role.
  return <DropdownInner {...rest} ref={ref} />;
});

type DropdownItemProps = React.ComponentProps<typeof MagneticDropdown.Item> & {
  /**
   * Controls which users can see this dropdown item.
   *
   * When omitted (default), no guards are applied — the item
   * is visible to all users.
   *
   * - `writeAccess`: Hidden from read-only users.
   * - `adminOnly`: Hidden from both read-only and read-write users.
   *   Only visible to users with the ADMIN role.
   */
  readonly scope?: ButtonScope;
};

const DropdownItem = ({ scope, ...rest }: DropdownItemProps) => {
  const { isReadOnly, isAdmin } = useContext(LoginContext);

  if (scope === ButtonScope.writeAccess && isReadOnly) {
    return null;
  }

  if (scope === ButtonScope.adminOnly && !isAdmin) {
    return null;
  }

  return <MagneticDropdown.Item {...rest} />;
};

export const Dropdown = Object.assign(DropdownComponent, {
  Item: DropdownItem
});
